import { useMachine } from '@xstate/react';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Stepper } from 'src/components/Auth/_components/Stepper';
import { withComponentSetup } from 'src/components/Auth/_lib/ComponentSetup';
import { FormsMessage } from 'src/components/Common/@electron/FormsMessage';
import useQueryParam from 'src/lib/Hooks/useQueryParam';
import useJurisdictionValue from '../../lib/useJurisdictionValue';
import { CompactFormsMessageContainer } from '../ProjectEstimator';
import { getJson } from '../api/getJson';
import { FindContractorType } from '../api/jsonTypes';
import { PreviousCancelButtons } from './components/Buttons';
import ContactForm from './components/ContactForm';
import { InfoLinkPropsType } from './components/ContractorCard/components/InfoLink';
import DisplayContractorCards from './components/DisplayContractorCards';
import FindContractorBackground from './components/FindContractorBackground';
import HeaderArea from './components/HeaderArea';
import IconCards from './components/IconCards';
import NoResultsModal from './components/NoResultsModal';
import SkeletonLoader from './components/SkeletonLoader';
import { Step6Form } from './components/Step6Form';
import StepItemAnimationContainer from './components/StepItemAnimationContainer';
import { SitecoreDataType } from './data';
import { findContractorMachine } from './machine';
import { ContactFormInput, FindContractorComponentProps, FindContractorProps } from './types';

const formatPreAmpName = (name: string) => {
  return name?.split('&')[0]?.toLowerCase();
};

const findServiceDataFromQP = (
  qpService: string,
  serviceCardData: FindContractorComponentProps['serviceCardData']
) => {
  const qp = qpService?.toLowerCase();
  // using for for loops insted of forEach to break early once found
  for (let i = 0; i < serviceCardData.length; i++) {
    const category = serviceCardData[i];
    const { services } = category;
    // there are "&"s in some of the names which is not picked up as part of the query param, so need to check text before that...
    const categoryPreAmpApiName = formatPreAmpName(category.apiName);
    const categoryPreAmpHeader = formatPreAmpName(category.categoryHeader);
    if (categoryPreAmpApiName === qp || categoryPreAmpHeader === qp) {
      const stepOneAnswer = category;
      // if qpService name matches a category (user clicked a special offer), a specific service has not been selected
      const stepTwoAnswer = { img: '', header: '', apiName: '' };
      return { stepOneAnswer, stepTwoAnswer };
    }
    for (let j = 0; j < services.length; j++) {
      const { apiName, header } = services[j];
      const servicePreAmpApiName = formatPreAmpName(apiName);
      const servicePreAmpHeader = formatPreAmpName(header);
      if (servicePreAmpApiName === qp || servicePreAmpHeader === qp) {
        const stepOneAnswer = category;
        const stepTwoAnswer = services[j];
        return { stepOneAnswer, stepTwoAnswer };
      }
    }
  }
  return null;
};

const FindContractorComponent = ({
  stepHeaders,
  serviceCardData,
  contactFormData,
  descriptionFormData,
  heroBgImg,
  busResCards,
  yesNoCards,
  previousButtonText,
  cancelButtonText,
  resultsData,
  noResultsData,
  tradeAllyResultCardData,
  referralIdLabel,
  foundRebateModalText,
  jurisdictionModalText,
  apiUrl,
  qpServiceData,
  qpOfferCode,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  jurisdiction,
  qpRebateDetails,
}: FindContractorComponentProps) => {
  const hasQPServiceData = !!qpServiceData?.stepTwoAnswer;
  const referralSource = qpOfferCode
    ? 'offers'
    : hasQPServiceData
    ? 'rebates'
    : document?.referrer?.startsWith('https://www.duke-energy.com')
    ? 'decom'
    : 'na';
  const [state, send] = useMachine(findContractorMachine, {
    context: {
      apiUrl,
      stepHeaders,
      analyticsOfferCode: qpOfferCode || qpRebateDetails?.name || 'na',
      referralSource,
      resultsTitle: resultsData.header,
      noResultsTitle: noResultsData.header,
    },
  });
  const {
    directionAnimating,
    activeStepIndex,
    stepOneAnswer,
    apiResponse,
    isGenerateReferralsButtonDisabled,
  } = state.context;
  const history = useHistory();
  const containerRef = useRef<HTMLDivElement>(null);
  const hasResults = state.value === 'results' && apiResponse.tradeAllyList?.length > 0;
  const hasNoResults = state.value === 'results' && !apiResponse.tradeAllyList?.length;
  const categoryName = stepOneAnswer?.categoryHeader || '';
  const stepItemNames = stepHeaders.map(step => {
    return step.name;
  });

  useEffect(() => {
    if (hasQPServiceData) {
      send({
        type: 'START_AT_STEP3',
        data: {
          stepOneAnswer: qpServiceData.stepOneAnswer,
          stepTwoAnswer: qpServiceData.stepTwoAnswer,
          stepThreeAnswer: { offerCode: qpOfferCode },
        },
      });
    }
  }, []);

  const goBack = () => {
    send({ type: 'PREVIOUS' });
  };

  const startOver = () => {
    // removing query params after start over
    history.replace({});
    send({ type: 'START_OVER' });
  };

  const handleModalCancel = () => {
    send({ type: 'CANCEL_MODAL' });
  };

  const stopAnimating = () => {
    send({ type: 'STOP_ANIM' });
    // if scrolled past top of heading text, scroll up after step changes
    const top = containerRef.current?.getBoundingClientRect()?.top || 0;
    if (top <= -195) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const sendContractorCardAnalytics = (data: InfoLinkPropsType['linkType']) => {
    send({ type: 'TRACK_CONTRACTOR_CARD_CLICK', data });
  };

  const sendRebateFoundModalAnalytics = (rebateName: string) => {
    send({
      type: 'TRACK_REBATE_MODAL_LOAD',
      data: { modalTitle: foundRebateModalText.title, analyticsOfferCode: rebateName },
    });
  };

  const handleSubmit = (step6Val: string) => {
    send({ type: 'SUBMIT', data: step6Val });
  };

  if (!hasResults && state.value !== 'error') {
    return (
      <FindContractorBackground bgImage={heroBgImg} containerRef={containerRef}>
        <div className="text-white text-center py-24 px-16 sm:pt-56 sm:px-56">
          <div className="container-2xl mb-64 md:mb-80">
            <div className="md:hidden">
              {activeStepIndex + 1} / {stepItemNames.length}
            </div>
            <Stepper activeIndex={activeStepIndex} steps={stepItemNames} percentComplete={0} />
          </div>
          {/* StepItemContainer will hide inactive steps, but each step's content needs to be a direct child of this.
      ids don't really matter, just keeping sections clearer for now.
      we could probably convert to more of a loop too, we'll see how much sense that makes when it's more built out */}
          <StepItemAnimationContainer
            activeStep={activeStepIndex}
            stopAnimating={stopAnimating}
            direction={directionAnimating}
          >
            <div id="stepOne">
              <HeaderArea {...stepHeaders[0]} />
              <IconCards
                cards={serviceCardData.map(cards => ({
                  header: cards.categoryHeader,
                  imgSrc: cards.categoryImg,
                  handleClick: () => {
                    send({ type: 'CONTINUE', data: cards });
                  },
                }))}
              />
            </div>
            <div id="stepTwo">
              <HeaderArea {...stepHeaders[1]} categoryName={categoryName} />
              <IconCards
                cards={
                  stepOneAnswer?.services.map(card => ({
                    header: card.header,
                    imgSrc: card.img,
                    handleClick: () => {
                      send({ type: 'CONTINUE', data: card });
                    },
                  })) || []
                }
              />
              <PreviousCancelButtons
                handleCancelClick={startOver}
                handlePreviousClick={goBack}
                previousButtonText={previousButtonText}
                cancelButtonText={cancelButtonText}
              />
            </div>
            <div id="stepThree">
              <HeaderArea {...stepHeaders[2]} />
              <div className="flex justify-center text-gray-dark text-left mt-24">
                <ContactForm
                  props={{ ...contactFormData, previousButtonText, cancelButtonText }}
                  handleCancel={startOver}
                  handleContinue={(data: ContactFormInput) => {
                    send({ type: 'CONTINUE', data });
                  }}
                  handlePrevious={(data: ContactFormInput) => {
                    if (hasQPServiceData) {
                      history.goBack();
                    } else {
                      send({ type: 'PREVIOUS', data });
                    }
                  }}
                  defaultValues={state.context.stepThreeAnswer}
                  rebateDetails={qpRebateDetails}
                />
              </div>
            </div>
            <div id="stepFour">
              <HeaderArea {...stepHeaders[3]} />
              <IconCards
                cards={busResCards?.map(card => ({
                  header: card.header,
                  imgSrc: card.img,
                  handleClick: () => {
                    send({ type: 'CONTINUE', data: card.header });
                  },
                }))}
              />
              <PreviousCancelButtons
                handleCancelClick={startOver}
                handlePreviousClick={goBack}
                previousButtonText={previousButtonText}
                cancelButtonText={cancelButtonText}
              />
            </div>
            <div id="stepFive">
              <HeaderArea {...stepHeaders[4]} />
              <IconCards
                cards={yesNoCards?.map(card => ({
                  header: card.header,
                  imgSrc: card.img,
                  handleClick: () => {
                    send({ type: 'CONTINUE', data: card.header });
                  },
                }))}
              />
              <PreviousCancelButtons
                handleCancelClick={startOver}
                handlePreviousClick={goBack}
                previousButtonText={previousButtonText}
                cancelButtonText={cancelButtonText}
              />
            </div>
            <div id="stepSix">
              <HeaderArea
                header={stepHeaders[5].header?.replace('xxx', categoryName)}
                subhead={stepHeaders[5].subhead}
                categoryName={categoryName}
              />
              <Step6Form
                goBack={(data: string) => {
                  send({ type: 'PREVIOUS', data });
                }}
                isGenerateReferralsButtonDisabled={isGenerateReferralsButtonDisabled}
                handleCancel={startOver}
                defaultValue={state.context.stepSixAnswer}
                handleFormSubmit={handleSubmit}
                fetchIsPending={state.value === 'fetchingReferrals'}
                formData={{ ...descriptionFormData, previousButtonText, cancelButtonText }}
                service={state.context.stepTwoAnswer}
                stateCode={state.context.stepThreeAnswer.stateCode}
                preselectedOffer={qpRebateDetails?.name}
                incomingJurisdiction={jurisdiction}
                sendRebateFoundModalAnalytics={sendRebateFoundModalAnalytics}
                foundRebateModalText={foundRebateModalText}
                jurisdictionModalText={jurisdictionModalText}
              />
              {hasNoResults && (
                <NoResultsModal
                  handleCancel={handleModalCancel}
                  {...{ ...noResultsData, cancelButtonText, startOver }}
                />
              )}
            </div>
          </StepItemAnimationContainer>
        </div>
      </FindContractorBackground>
    );
  } else if (hasResults) {
    return (
      <FindContractorBackground bgImage={heroBgImg} containerRef={containerRef}>
        <DisplayContractorCards
          results={apiResponse}
          headerText={resultsData.header}
          tooltipText={resultsData.tooltip}
          introText={resultsData.intro}
          categoryImg={state.context.stepOneAnswer?.categoryImg}
          categoryHeader={state.context.stepOneAnswer?.categoryHeader}
          tradeAllyResultCardData={tradeAllyResultCardData}
          referralIdLabel={referralIdLabel}
          startOver={startOver}
          startOverButtonText={resultsData.startOverButtonText}
          sendContractorCardAnalytics={sendContractorCardAnalytics}
        />
      </FindContractorBackground>
    );
  } else {
    return (
      <div className="bg-blue-dark py-80 px-16 flex flex-1 flex-col">
        <CompactFormsMessageContainer>
          <FormsMessage
            variant="info"
            size="compact"
            title="Something went wrong."
            description="We're having trouble displaying your contractor results right now. Please try again later. (444)"
          />
        </CompactFormsMessageContainer>
      </div>
    );
  }
};

const FindContractor = ({ jsonUrl, apiUrl }: FindContractorProps) => {
  const [json, setJson] = useState<FindContractorType>();
  const [jsonError, setJsonError] = useState(false);
  const [qpServiceName] = useQueryParam('fidService') || '';
  const [qpOfferCode] = useQueryParam('offerCode') || '';
  const [rebate] = useQueryParam('rebate');
  const [rebateAmount] = useQueryParam('rebateAmount');
  // useAppContext doesn't work for this, no primary nav
  const jurisdiction = useJurisdictionValue();

  useEffect(() => {
    const doFetch = async () => {
      getJson(jsonUrl).then(res => {
        if (res) {
          setJson(res);
        } else {
          setJsonError(true);
        }
      });
    };
    doFetch();
  }, []);

  if (json) {
    return (
      <FindContractorComponent
        {...json}
        apiUrl={apiUrl}
        qpServiceData={
          qpServiceName ? findServiceDataFromQP(qpServiceName, json.serviceCardData) : null
        }
        qpOfferCode={qpOfferCode}
        jurisdiction={jurisdiction}
        qpRebateDetails={
          rebate && rebateAmount ? { name: rebate, amount: rebateAmount } : undefined
        }
      />
    );
  } else if (jsonError) {
    return (
      <div className="bg-blue-dark py-80 px-16 flex flex-1 flex-col">
        <CompactFormsMessageContainer>
          <FormsMessage
            variant="info"
            title="Something went wrong."
            description="We're having trouble displaying Find a Contractor right now. Please try again later. (111)"
            size="compact"
          />
        </CompactFormsMessageContainer>
      </div>
    );
  }
  return <SkeletonLoader />;
};

const resolver = (props: SitecoreDataType) => {
  const jsonUrl =
    props.fields.JsonData.value.href ||
    `/_/media/json/find it duke/contractor search/fid data.json?rev=${Math.random()
      .toString()
      .slice(2, 6)}`;
  const apiUrl =
    props.fields.Endpoint.fields.Phrase.value || '/facade/api/AspireRewards/GenerateReferrals';
  return {
    jsonUrl,
    apiUrl,
  };
};

export { FindContractorComponent };
export default withComponentSetup({
  resolver,
  Component: FindContractor,
});
